import React, {useState, useCallback} from 'react';
import {View, StyleSheet, FlatList, TouchableOpacity, RefreshControl, ActivityIndicator} from 'react-native';
import {Text, Snackbar} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {workerApi} from '../../services/api/workerApi';
import {colors, spacing} from '../../theme';

const TaskAssignmentsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await workerApi.getTaskAssignments();
      setData(response.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load task assignments');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  const handleRowPress = item => {
    navigation.navigate('TaskAssignmentDetail', {
      workerId: item.workerId,
      workerName: item.workerName,
      taskTypeId: item.taskTypeId,
      taskTypeName: item.taskTypeName,
    });
  };

  const renderItem = ({item, index}) => (
    <TouchableOpacity activeOpacity={0.7} onPress={() => handleRowPress(item)}>
      <View style={[styles.dataRow, index % 2 === 1 && styles.dataRowAlt]}>
        <Text style={[styles.cell, styles.nameCell]}>{item.workerName}</Text>
        <Text style={[styles.cell, styles.roleCell]}>{item.workerRole}</Text>
        <Text style={[styles.cell, styles.taskCell]}>{item.taskTypeName}</Text>
        <View style={styles.jobsCell}>
          <Text style={styles.jobsText}>{item.jobCount}</Text>
          <MaterialCommunityIcons name="chevron-right" size={20} color={colors.textSecondary} />
        </View>
      </View>
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.tableHeader}>
        <Text style={[styles.headerCell, styles.nameCell]}>Worker{'\n'}Name</Text>
        <Text style={[styles.headerCell, styles.roleCell]}>Job Role</Text>
        <Text style={[styles.headerCell, styles.taskCell]}>Task{'\n'}Name</Text>
        <Text style={[styles.headerCell, styles.jobsHeaderCell]}>#Jobs</Text>
      </View>

      <FlatList
        data={data}
        keyExtractor={(item, index) => `${item.workerId}-${item.taskTypeId}-${index}`}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => fetchData(true)} />
        }
        ListEmptyComponent={
          <Text style={styles.emptyText}>{t('common.noData')}</Text>
        }
      />

      <Snackbar
        visible={!!error}
        onDismiss={() => setError('')}
        duration={3000}>
        {error}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.background},
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
  headerCell: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.white,
    textAlign: 'center',
  },
  nameCell: {flex: 2, textAlign: 'left'},
  roleCell: {flex: 2, textAlign: 'center'},
  taskCell: {flex: 2, textAlign: 'center'},
  jobsHeaderCell: {flex: 1.2, textAlign: 'center'},
  jobsCell: {flex: 1.2, flexDirection: 'row', alignItems: 'center', justifyContent: 'center'},
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  dataRowAlt: {backgroundColor: colors.background},
  cell: {fontSize: 13, color: colors.text, textAlign: 'center'},
  jobsText: {fontSize: 13, fontWeight: '600', color: colors.text, marginRight: spacing.xs},
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
    fontSize: 16,
  },
});

export default TaskAssignmentsScreen;
